// @flow
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
// Components
import { Button, RadioButton } from '../../../common';
import CheckoutErrors from '../CheckoutErrors';
import DeliveryPicker from './DeliveryPicker';
import Takeout from './Takeout';
import PanelCard from '../PanelCard';
// Analytics
import * as Analytics from '../../../common/analytics';
// Types
import type { ShippingRate, OrderAddress } from '../../types';
// Styles
import './DeliveryPane.css';
import type { TakeoutAddress } from '../../types/TakeoutAddress';

type Props = {
  errors: string[],
  loading: boolean,
  active: boolean,
  selected: ?ShippingRate,
  orderAddress: ?OrderAddress,
  shippingRates: ShippingRate[],
  onSubmit: ShippingRate => void,
  dismissErrors: () => void,
  transitionToDelivery: () => void,
  isCompleted: boolean,
  takeoutAddress: TakeoutAddress,
};

const DeliveryPane = (props: Props) => {
  const [selectedShippingRate, setSelectedShippingRate] =
    useState<ShippingRate | null>(
      props.shippingRates.length > 0 ? props.shippingRates[0] : null,
    );
  const [deliveryType, setDeliveryType] = useState<
    'entrega_agendada' | 'retirada',
  >('entrega_agendada');

  const submitShippingRate = (e: Event | undefined) => {
    e && e.preventDefault();
    if (selectedShippingRate) {
      props.onSubmit(selectedShippingRate);
    }
  };

  const selectShippingRate = (sr: ShippingRate) => {
    setSelectedShippingRate(sr);
  };

  const handleSelectDeliveryType = (
    type: string,
    takeoutShippingRate?: ShippingRate,
  ) => {
    const shippingRate = type === 'retirada' ? takeoutShippingRate : null;
    setSelectedShippingRate(shippingRate);
    setDeliveryType(type);
  };

  const takeoutShippingRate = props.shippingRates.filter(
    s => s.shippingMethodCode === 'takeout',
  );
  const shippingRatesWithoutTakeout = props.shippingRates.filter(
    s => s.shippingMethodCode !== 'takeout',
  );

  const isTakeout =
    props.orderAddress?.address1 === props.takeoutAddress?.address1 &&
    props.orderAddress?.address2 === props.takeoutAddress?.address2;
  const currentShippingRates = isTakeout
    ? takeoutShippingRate
    : shippingRatesWithoutTakeout;

  useEffect(() => {
    if (
      props.shippingRates.length > 0 &&
      selectedShippingRate &&
      !_.find(props.shippingRates, {
        id: selectedShippingRate.id,
      })
    ) {
      setSelectedShippingRate(null);
    }
    // eslint-disable-next-line
  }, [props.shippingRates.length]);

  useEffect(() => {
    setSelectedShippingRate(null);
  }, [props.active]);

  return (
    <>
      <PanelCard
        isOpen={props.active}
        number={2}
        loading={props.loading}
        title="Agende sua entrega"
        handleClick={props.transitionToDelivery}
        isCompleted={props.isCompleted}
        isLoading={props.loading}
      >
        {props.active && (
          <div>
            <CheckoutErrors
              errors={props.errors}
              dismiss={props.dismissErrors}
              showErrorFields={true}
            />

            {(deliveryType === 'entrega_agendada' || !takeoutShippingRate) && (
              <>
                {currentShippingRates.length > 0 ? (
                  <>
                    <h3 className="delivery-pane-title">
                      Escolha a data e horário da{' '}
                      {isTakeout ? 'retirada' : 'entrega'}
                    </h3>
                    <DeliveryPicker
                      shippingRates={currentShippingRates}
                      selected={selectedShippingRate}
                      onSelect={selectShippingRate}
                    />
                  </>
                ) : (
                  <h3 className="delivery-pane-title">
                    Sem horários disponíveis para{' '}
                    {isTakeout ? 'retirada' : 'entrega'}.
                  </h3>
                )}
              </>
            )}
            <div className="delivery-pane-continue-container">
              {deliveryType === 'retirada' && (
                <Takeout takeoutShippingRate={takeoutShippingRate} />
              )}
              <div className="delivery-pane-checkout-continue-button">
                <Button
                  buttonType="button-primary"
                  icon=""
                  text={
                    isTakeout
                      ? 'Escolher data de retirada'
                      : 'Escolher data de entrega'
                  }
                  disabled={!selectedShippingRate}
                  onClick={(e: Event) => {
                    submitShippingRate(e);
                    Analytics.logConfirmedDelivery();
                  }}
                  loading={props.loading}
                />
              </div>
            </div>
          </div>
        )}
      </PanelCard>
    </>
  );
};

export default DeliveryPane;
